import { useState, useCallback } from "react";
import toast from "react-hot-toast";
import { supabase } from "../lib/supabase";
import { List, Tag } from "../types";

export interface ProposedTodo {
  title: string;
  list_id: string | null;
  priority: number;
  description: string;
  scheduled_date: string | null;
  scheduled_time: string | null;
  deadline_date: string | null;
  subtasks: { title: string }[];
  tag_ids: string[];
  confidence: number;
  /** Set on the review card. Absent means this card is still a new task. */
  parent_task_id?: string | null;
}

interface VoiceToTodoResponse {
  transcript: string;
  todos: ProposedTodo[];
  error?: string;
}

export interface UseVoiceToTodoReturn {
  isProcessing: boolean;
  transcript: string | null;
  proposedTodos: ProposedTodo[];
  error: string | null;
  processAudio: (
    audioBlob: Blob,
    lists: List[],
    tags: Tag[]
  ) => Promise<boolean>;
  processText: (text: string, lists: List[], tags: Tag[]) => Promise<boolean>;
  clearResults: () => void;
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:audio/webm;base64,")
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function fetchProposedTodosFromText(
  text: string,
  lists: List[],
  tags: Tag[]
): Promise<{ transcript: string; todos: ProposedTodo[] }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) throw new Error("Not authenticated - please sign in");

  const { data, error: fnError } =
    await supabase.functions.invoke<VoiceToTodoResponse>("voice-to-todo", {
      body: {
        text,
        lists: lists.map((l) => ({ id: l.id, name: l.name, emoji: l.emoji })),
        tags: tags.map((t) => ({ id: t.id, name: t.name, color: t.color })),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

  if (fnError) throw new Error(fnError.message || "Failed to process text");
  if (!data) throw new Error("No response from server");
  if (data.error) throw new Error(data.error);

  return { transcript: data.transcript, todos: data.todos };
}

export function useVoiceToTodo(): UseVoiceToTodoReturn {
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [proposedTodos, setProposedTodos] = useState<ProposedTodo[]>([]);
  const [error, setError] = useState<string | null>(null);

  const processAudio = useCallback(
    async (
      audioBlob: Blob,
      lists: List[],
      tags: Tag[]
    ): Promise<boolean> => {
      setIsProcessing(true);
      setError(null);
      setTranscript(null);
      setProposedTodos([]);

      try {
        // Convert blob to base64
        const audioBase64 = await blobToBase64(audioBlob);

        // Get the current session for auth
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;

        if (!accessToken) {
          throw new Error("Not authenticated - please sign in");
        }

        // Call the edge function with explicit auth header
        const { data, error: fnError } = await supabase.functions.invoke<VoiceToTodoResponse>(
          "voice-to-todo",
          {
            body: {
              audio: audioBase64,
              lists: lists.map((l) => ({
                id: l.id,
                name: l.name,
                emoji: l.emoji,
              })),
              tags: tags.map((t) => ({
                id: t.id,
                name: t.name,
                color: t.color,
              })),
            },
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (fnError) {
          throw new Error(fnError.message || "Failed to process audio");
        }

        if (!data) {
          throw new Error("No response from server");
        }

        if (data.error) {
          throw new Error(data.error);
        }

        setTranscript(data.transcript);
        setProposedTodos(data.todos);

        return data.todos.length > 0;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to process audio";
        setError(message);
        console.error("Voice-to-todo error:", err);
        toast.error("Failed to process voice recording. Please try again.");
        return false;
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  const processText = useCallback(
    async (text: string, lists: List[], tags: Tag[]): Promise<boolean> => {
      setIsProcessing(true);
      setError(null);
      setTranscript(null);
      setProposedTodos([]);
      try {
        const { transcript: t, todos } = await fetchProposedTodosFromText(
          text,
          lists,
          tags
        );
        setTranscript(t);
        setProposedTodos(todos);
        return todos.length > 0;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to process text";
        setError(message);
        return false;
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  const clearResults = useCallback(() => {
    setTranscript(null);
    setProposedTodos([]);
    setError(null);
  }, []);

  return {
    isProcessing,
    transcript,
    proposedTodos,
    error,
    processAudio,
    processText,
    clearResults,
  };
}
